// Segmented controls ([data-segmented]): the chosen option (a button with aria-pressed="true" or a link with
// aria-current="true") is drawn by a lime copy of the group sitting on top, clipped to that option. On change the
// clip slides to the next option, so the highlight and its dark label move together instead of the colour snapping.
// Only clip-path animates (style.css, .segmented-overlay).

const isChosen = (option) =>
	option.getAttribute("aria-pressed") === "true" || option.getAttribute("aria-current") === "true";

const clipTo = (overlay, option) => {
	const right = overlay.offsetWidth - option.offsetLeft - option.offsetWidth - 1;
	const bottom = overlay.offsetHeight - option.offsetTop - option.offsetHeight - 1;
	return `inset(${option.offsetTop + 1}px ${right}px ${bottom}px ${option.offsetLeft + 1}px round 5px)`;
};

export function initSegmentedControl(group) {
	if (group.querySelector(":scope > .segmented-overlay")) return;

	const overlay = group.cloneNode(true);
	for (const name of ["id", "role", "aria-label", "data-segmented"]) overlay.removeAttribute(name);
	overlay.classList.add("segmented-overlay");
	overlay.setAttribute("aria-hidden", "true");
	overlay.inert = true;
	// The copies keep only their look: no id, href or data-* hooks, so page scripts selecting the options never
	// see them; every copy is drawn chosen.
	for (const copy of overlay.children) {
		for (const name of copy.getAttributeNames()) {
			if (["id", "href", "disabled"].includes(name) || name.startsWith("data-"))
				copy.removeAttribute(name);
		}
		copy.setAttribute(copy.tagName === "A" ? "aria-current" : "aria-pressed", "true");
	}
	group.append(overlay);

	const options = [...group.children].filter((child) => child !== overlay);
	// One tab stop per group, on the chosen option.
	const rove = (current) => {
		for (const option of options) option.tabIndex = option === current ? 0 : -1;
	};
	const place = (animate) => {
		const chosen = options.find(isChosen);
		rove(chosen ?? options[0]);
		const appearing = overlay.hidden;
		overlay.hidden = !chosen;
		if (!chosen) return;
		overlay.toggleAttribute("data-instant", !animate || appearing);
		overlay.style.clipPath = clipTo(overlay, chosen);
	};

	// Arrows, Home and End move the focus; Enter or Space chooses (a choice can run a whole report).
	const STEPS = { ArrowLeft: -1, ArrowUp: -1, ArrowRight: 1, ArrowDown: 1 };
	group.addEventListener("keydown", (event) => {
		const enabled = options.filter((option) => !option.disabled);
		const index = enabled.indexOf(document.activeElement);
		if (index === -1 || !(event.key in STEPS || event.key === "Home" || event.key === "End"))
			return;
		event.preventDefault();
		const next =
			event.key === "Home"
				? 0
				: event.key === "End"
					? enabled.length - 1
					: (index + STEPS[event.key] + enabled.length) % enabled.length;
		rove(enabled[next]);
		enabled[next].focus();
	});

	new MutationObserver(() => place(true)).observe(group, {
		subtree: true,
		attributeFilter: ["aria-pressed", "aria-current"],
	});
	const resize = new ResizeObserver(() => place(false));
	for (const option of options) resize.observe(option);
	overlay.hidden = true;
	place(false);
}
